import { Auth, type CognitoUser } from '@aws-amplify/auth'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { CognitoIdToken } from 'amazon-cognito-identity-js'
import axios, { setAccessToken, setSpoofToken } from 'api/apiCommon'
import { setSpoofingTokenToPayload } from 'common/authentication/auth.slice'
import { logout } from 'store/app.slice'
import { type UserInfoData } from 'common/utils/types'

import { type ConfirmSignUpForm } from './confirmSignUp/ConfirmSignUp'
import { type ForgotPasswordForm } from './forgotPassword/ForgotPassword'
import { type ResetPasswordForm } from './resetPassword/ResetPassword'
import { type LoginForm } from './login/Login'

export const refreshToken = async (): Promise<string> => {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

export const checkTokenIsExpired = (token: string): boolean => {
  const decodedToken = new CognitoIdToken({ IdToken: token })
  if (decodedToken) {
    return decodedToken.getExpiration() < Math.floor(Date.now() / 1000)
  }
  return true
}

export const getAuthUser = createAsyncThunk('auth/getAuthUser', async () => {
  return await Auth.currentAuthenticatedUser()
    .then(async (user: CognitoUser) => {
      const token = await refreshToken()
      setAccessToken(token)
      return user
    })
    .catch(() => {
      // no current user
      return null
    })
})

interface ConfirmSignUpArgs {
  user: CognitoUser | null
  formValues: ConfirmSignUpForm
}
export const confirmSignUp = createAsyncThunk(
  'auth/confirmSignUp',
  async ({ user, formValues }: ConfirmSignUpArgs): Promise<CognitoUser> => {
    const { newPassword } = formValues
    return await new Promise<CognitoUser>((resolve, reject) => {
      Auth.completeNewPassword(user, newPassword)
        .then(async (user: CognitoUser) => {
          await Auth.signOut()
          await Auth.signIn(user.getUsername(), newPassword)
          void refreshToken().then((token) => {
            setAccessToken(token)
            resolve(user)
          })
        })
        .catch((err) => {
          console.error('completeNewPassword err: ', err)
          reject(err)
        })
    })
  },
)

export const resendSignUp = createAsyncThunk(
  'auth/resendSignUp',
  async ({ email }: Pick<ConfirmSignUpForm, 'email'>) => {
    return await Auth.resendSignUp(email)
  },
)

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }: LoginForm): Promise<{ user: CognitoUser; isAuthenticated: boolean }> => {
    return await new Promise((resolve, reject) => {
      Auth.signIn(email, password)
        .then(async (user) => {
          if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            resolve({ user, isAuthenticated: false })
            return
          }
          await refreshToken().then((token) => {
            setAccessToken(token)
            resolve({ user, isAuthenticated: true })
          })
        })
        .catch((err) => {
          // no current user
          reject(err)
        })
    })
  },
)

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }: ForgotPasswordForm) => {
    return await Auth.forgotPassword(email).catch((err) => {
      switch (err.code) {
        case 'NotAuthorizedException':
          throw new Error('Korisnik sa datim mejlom / šifrom ne postoji.')
        default:
          throw new Error('Greška pri resetovanju lozinke.')
      }
    })
  },
)

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, code, password }: ResetPasswordForm) => {
    return await Auth.forgotPasswordSubmit(email, code, password)
  },
)

export const signOut = createAsyncThunk('auth/signOut', async (_, { dispatch }) => {
  await Auth.signOut()
  // clear redux state
  dispatch(logout())
})

export const getUserInfo = createAsyncThunk(
  '/users/me',
  async (processing?: boolean): Promise<UserInfoData> => {
    return await new Promise((resolve, reject) => {
      if (processing) {
        axios
          .get<UserInfoData>('users/me')
          .then((data) => {
            resolve(data)
          })
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      } else {
        axios
          .get<UserInfoData>(`${process.env.REACT_APP_USERS_API_URL ?? ''}/users/me`)
          .then((data) => {
            resolve(data)
          })
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      }
    })
  },
)

export const startSpoofingSession = createAsyncThunk(
  '/users/impersonation-token',
  async (request: { userId: string }): Promise<string> => {
    return await axios
      .post('/users/impersonation-token', request)
      .then((response) => {
        setSpoofToken(response)
        return response
      })
      .catch((err) => {
        return err
      })
  },
)

export const resetSpoofingToken = createAsyncThunk('auth/resetSpoofingToken', async (_, { dispatch }) => {
  dispatch(setSpoofingTokenToPayload(undefined))
  setSpoofToken(undefined)
  localStorage.removeItem('spoofSessionToken')
})
